import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import { http } from '../../../../clients/http/http.client';
import { HTTP_ENDPOINTS } from '../../../../clients/clients.constants';
import Loader from '../../../Inputs/Loader/Loader';
import { useLocation } from 'react-router-dom';
import ToggleViewButton from './ToggleViewButton';

const CartStatisticsView = ({ siteId, onBackToChart, isMonthly, handleToggle }) => {
  const [cartStats, setCartStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();

  const colors = ['#89D8C2', '#E89585', '#B6CFF1', '#32a852', '#328da8', '#3012de'];

  const buildQueryParams = ({ siteId, locationSearch }) => {
    const params = new URLSearchParams();
    if (siteId) params.append('siteId', siteId);
    if (locationSearch.includes('arrc-inventory')) {
      params.delete('siteId');
      params.append('siteId', 1);
    }
    return params.toString() ? `?${params.toString()}` : '';
  };

  useEffect(() => {
    const fetchCartStats = async () => {
      if (!siteId) return;
      
      try {
        setLoading(true);
        setError(null);
        const qs = buildQueryParams({
          siteId,
          locationSearch: location.search,
        });
        const res = await http.client.get(`${HTTP_ENDPOINTS.ALERTS_CART_STATISTICS_ONLY}${qs}`);
        
        // Transform the data to match the expected format
        const transformedData = res.map((item, index) => ({
          name: item.name,
          value: item.value,
          color: colors[index % colors.length]
        }));
        
        setCartStats(transformedData);
      } catch (e) {
        setError('Failed to load cart statistics');
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    fetchCartStats();
  }, [siteId, location.search]);

  const renderCartStatistics = () => (
    <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', alignItems: 'flex-start' }}>
      {cartStats.map((entry, idx) => (
        entry.name !== 'Total' && (
          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CircleIcon sx={{ color: entry.color, fontSize: 12 }} />
            <Typography sx={{ fontSize: 12 }}>{entry.name}</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{entry.value}</Typography>
          </Box>
        )
      ))}
    </Box>
  );

  if (loading) {
    return <Loader sx={{ width: '100%', height: '100%' }} />;
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
        <Button 
          onClick={onBackToChart}
          sx={{ mt: 1 }}
          variant="outlined"
        >
          Back to Chart
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 600,
            color: '#1B3665',
          }}
        >
          Cart Statistics
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <ToggleViewButton isMonthly={isMonthly} onToggle={handleToggle} />
        </Box>
      </Box>
      {renderCartStatistics()}
    </Box>
  );
};

export default CartStatisticsView; 